import assetValidation, { editFormValidationSchema } from 'domain/finance/assets/createupdate/assetValidation';
import { useFinanceApi, useKernelApi } from 'infrastructure/api';
import { ResourceData, ResourceId, useCrudService, useTimelineService } from 'framework/crud';
import { useSingletonPromise } from 'framework/utils';
import { useEmployeeService, useEmployeeNamesService } from 'domain/shared/employee';
import { cloneDeep } from 'lodash';

export const READ_ASSET_LIST = 'COMPANY_READ_ASSET_LIST';
export const READ_ASSET = 'COMPANY_READ_ASSET';
export const DISPOSE_ASSET = 'COMPANY_DISPOSE_ASSET';
export const WRITE_OFF_ASSET = 'COMPANY_WRITE_OFF_ASSET';
export const CREATE_ASSET = 'COMPANY_CREATE_ASSET';
export const UPDATE_ASSET = 'COMPANY_UPDATE_ASSET';
export const UPDATE_ASSET_ASSIGNMENT = 'COMPANY_UPDATE_ASSET_ASSIGNMENT';
export const PERFORM_ASSET_COST_PRORATING = 'COMPANY_PERFORM_ASSET_COST_PRORATING';
export const READ_TIMELINE = 'COMPANY_SEARCH_ASSET_NOTES';
export const ASSET_PATH = '/assets';
const RESOURCE_URL = '/asset';

const ASSET_CATEGORIES = {
  INDIVIDUAL_EQUIPMENT: 'INDIVIDUAL_EQUIPMENT',
  OFFICE_EQUIPMENT: 'OFFICE_EQUIPMENT',
  COST_CENTER_EQUIPMENT: 'COST_CENTER_EQUIPMENT',
  FURNITURE: 'OFFICE_FURNITURE',
  COMPANY_INTANGIBLE_ASSETS: 'COMPANY_INTANGIBLE_ASSETS',
  COST_CENTER_INTANGIBLE_ASSETS: 'COST_CENTER_INTANGIBLE_ASSETS',
};

export const ASSET_CATEGORIES_OPTIONS = {
  [ASSET_CATEGORIES.INDIVIDUAL_EQUIPMENT]: 'Individual Equipment',
  [ASSET_CATEGORIES.OFFICE_EQUIPMENT]: 'Office Equipment',
  [ASSET_CATEGORIES.COST_CENTER_EQUIPMENT]: 'Cost Center Equipment',
  [ASSET_CATEGORIES.FURNITURE]: 'Furniture',
  [ASSET_CATEGORIES.COMPANY_INTANGIBLE_ASSETS]: 'Company Intangible Assets',
  [ASSET_CATEGORIES.COST_CENTER_INTANGIBLE_ASSETS]: 'Cost Center Intangible Assets',
};

export const ASSET_STATUS = {
  REGISTERED: 'REGISTERED',
  ON_BALANCE: 'ON_BALANCE',
  ON_BALANCE_COST_PRORATED: 'ON_BALANCE_COST_PRORATED',
  WRITE_OFF_AFTER_PURCHASE: 'WRITE_OFF_AFTER_PURCHASE',
  WRITE_OFF_AFTER_COST_PRORATING: 'WRITE_OFF_AFTER_COST_PRORATING',
  DISPOSED: 'DISPOSED',
};

export const ASSET_STATUS_OPTIONS = {
  [ASSET_STATUS.REGISTERED]: 'Registered',
  [ASSET_STATUS.ON_BALANCE]: 'On Balance',
  [ASSET_STATUS.ON_BALANCE_COST_PRORATED]: 'On Balance Cost Prorated',
  [ASSET_STATUS.WRITE_OFF_AFTER_PURCHASE]: 'Write-off after purchase',
  [ASSET_STATUS.WRITE_OFF_AFTER_COST_PRORATING]: 'Write-off after cost prorating',
  [ASSET_STATUS.DISPOSED]: 'Disposed',
};

const INTANGIBLE_ASSETS_CATEGORY_TYPES = {
  SOFTWARE_LICENSE_KEY: 'Software License Key',
  LICENSE: 'License',
  SUBSCRIPTION: 'Subscription',
  CERTIFICATE: 'Certificate',
  OTHER_INTANGIBLE_ASSETS: 'Other',
};

export const ASSET_CATEGORIES_TYPES: Dictionary<Dictionary<string>> = {
  [ASSET_CATEGORIES.INDIVIDUAL_EQUIPMENT]: {
    LAPTOP: 'Laptop',
    MOUSE: 'Mouse',
    HEADSET: 'Headset',
    WEBCAM: 'Webcam',
    MONITOR: 'Monitor',
    SYSTEM_UNIT: 'System Unit',
    SPEAKER: 'Speaker',
    TABLET: 'Tablet',
    KEYBOARD: 'Keyboard',
    ADAPTER: 'Adapter',
    OTHER_INDIVIDUAL_EQUIPMENT: 'Other',
  },
  [ASSET_CATEGORIES.OFFICE_EQUIPMENT]: {
    COFFEE_MACHINE: 'Coffee Machine',
    ELECTRIC_KETTLE: 'Electric Kettle',
    PRINTER: 'Printer',
    OTHER_OFFICE_EQUIPMENT: 'Other',
  },
  [ASSET_CATEGORIES.FURNITURE]: {
    TABLE: 'Table',
    ARMCHAIR: 'Armchair',
    WARDROBE: 'Wardrobe',
    SHELVING: 'Shelving',
    PICTURE: 'Picture',
    OTHER_OFFICE_FURNITURE: 'Other',
  },
  [ASSET_CATEGORIES.COMPANY_INTANGIBLE_ASSETS]: INTANGIBLE_ASSETS_CATEGORY_TYPES,
  [ASSET_CATEGORIES.COST_CENTER_INTANGIBLE_ASSETS]: INTANGIBLE_ASSETS_CATEGORY_TYPES,
  [ASSET_CATEGORIES.COST_CENTER_EQUIPMENT]: {
    TEST_DEVICE: 'Test device',
    OTHER_COST_CENTER_EQUIPMENT: 'Other',
  },
};

export const ASSET_CONDITIONS = {
  NEW: 'Brand new',
  USED: 'Used, but still good enough to use',
  BROKEN: 'Broken, eligible for repair',
  LOST: 'Completely lost',
  UNAVAILABLE: 'Unavailable for provision',
};

const isEquipmentCategory = (category: string) =>
  [ASSET_CATEGORIES.INDIVIDUAL_EQUIPMENT, ASSET_CATEGORIES.OFFICE_EQUIPMENT, ASSET_CATEGORIES.FURNITURE].includes(
    category,
  );

export const isExpandedEquipmentCategory = (category: string) =>
  category === ASSET_CATEGORIES.COST_CENTER_EQUIPMENT || isEquipmentCategory(category);

export const isIntangibleAsset = (category: string) =>
  [ASSET_CATEGORIES.COMPANY_INTANGIBLE_ASSETS, ASSET_CATEGORIES.COST_CENTER_INTANGIBLE_ASSETS].includes(category);

export const isSubscription = (type: string) => type === 'SUBSCRIPTION';

export const isAutoRenewSubscription = (subscriptionDetails: any) => subscriptionDetails?.isAutoRenew;

type UsefulLifePeriodValue = {
  amount?: number | null;
  timeUnit?: string | null;
};

type GetBusinessRuleRequest = {
  category: string;
  type: string;
  name: string;
  purchaseDate: string;
  customUsefulLifePeriod?: UsefulLifePeriodValue | null;
};

const useAssetService = () => {
  const { getFullName } = useEmployeeService();
  const { getEmployeeNameDataById } = useEmployeeNamesService();
  const { sendGetRequest, sendPostRequest, sendPutRequest } = useFinanceApi();

  const {
    search: baseSearch,
    create: initialCreate,
    getById: initialGetById,
    update: initialUpdate,
    ...baseCrud
  } = useCrudService({
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: useFinanceApi,
  });
  const { getPermissions } = useCrudService({
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: useKernelApi,
  });

  const { search: initialSearchTimelineItems, create: initialCreateTimelineItem } = useTimelineService({
    parentEntityUrl: `${RESOURCE_URL}/:id`,
    useService: useFinanceApi,
  });

  const searchTimelineItems = async (entityId: ResourceId, request: ResourceData) => {
    const response = await initialSearchTimelineItems(entityId, request);

    return {
      ...response,
      result: response.result.map((item: any) => ({ ...item, createdById: item.createdBy, timelineId: item.id })),
    };
  };

  const createTimelineItem = async (entityId: ResourceId, timelineEntityType: string, data: ResourceData) => {
    const response = (await initialCreateTimelineItem(entityId, timelineEntityType, data)) as any;

    return {
      ...response,
      createdById: response.createdBy,
      timelineId: response.id,
    };
  };

  const search = (request: ResourceData) => {
    const searchParams = cloneDeep(request);
    const filters = searchParams.filter || {};

    const searchRequestFilters = Object.entries(filters).map((filter: any) => {
      const [key, searchOption] = filter;
      const [condition, value] = Object.entries(searchOption)[0] as any;

      if (key === 'specification:search') {
        return {
          filterType: 'FILTER_GROUP',
          condition: 'OR',
          filters: [
            {
              filterType: 'FILTER',
              property: 'name',
              operator: 'ct',
              value,
            },
            {
              filterType: 'FILTER',
              property: 'inventoryNumber',
              operator: 'ct',
              value,
            },
          ],
        };
      }

      if (key === 'currentAssignmentStatus') {
        return {
          filterType: 'FILTER',
          property: 'specification:currentAssignmentStatus',
          operator: value === 'YES' || value[0] === 'YES' ? 'eq' : 'ne',
          value: 'ACTIVE',
        };
      }

      return {
        filterType: 'FILTER',
        property: key,
        operator: condition,
        value,
      };
    });

    const advancedSearchRequest = {
      pageNumber: searchParams.pageNumber,
      pageSize: searchParams.pageSize,
      sort: searchParams.sort,
      type: 'advanced',
      filterGroup: {
        filterType: 'FILTER_GROUP',
        condition: 'AND',
        filters: searchRequestFilters,
      },
    };

    return baseSearch(advancedSearchRequest);
  };

  const create = async ({ cost, assignmentDetails: initialAssignmentDetails, ...data }: any) => {
    let assignmentDetails = { ...(initialAssignmentDetails || {}) };

    if (assignmentDetails.currentOwner) {
      delete assignmentDetails.currentOwnerName;
      delete assignmentDetails.currentOwnerFullName;
    } else {
      assignmentDetails = null;
    }

    return initialCreate({
      ...data,
      cost: {
        ...cost,
        currency: cost.currency === 'BYN' ? 'BYR' : cost.currency,
      },
      assignmentDetails,
    });
  };

  const getBalanceAccounts = useSingletonPromise('search-asset-balance-accounts', async () => {
    const response = await sendGetRequest('/asset/accounts/balance');
    const result = await response.json();

    return {
      result,
      totalElements: result.length,
      totalPages: 1,
    };
  });

  const getExpenseAccounts = useSingletonPromise('search-asset-expense-accounts', async () => {
    const response = await sendGetRequest('/asset/accounts/expense');
    const result = await response.json();

    return {
      result,
      totalElements: result.length,
      totalPages: 1,
    };
  });

  const getBusinessRules = async () => {
    const response = await sendGetRequest('/asset/business-rules-metadata/useful-life');

    return response.json();
  };

  const getBusinessRule = async (payload: GetBusinessRuleRequest) => {
    const res = await sendPostRequest('/asset/business-rules/search', payload);

    if (res.status === 204) return null;

    return res.json();
  };

  const getValidationSchema = () => Promise.resolve(assetValidation);

  const getUpdateValidationSchema = () => Promise.resolve(editFormValidationSchema);

  const getById = async (id: string) => {
    const { assignmentDetails, ...data } = await initialGetById(id);

    if (assignmentDetails?.currentOwner) {
      try {
        const employeeName = (await getEmployeeNameDataById(assignmentDetails.currentOwner)) || {};
        assignmentDetails.currentOwnerName = employeeName.nameEn;
        assignmentDetails.currentOwnerFullName = getFullName(employeeName.nameEn);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return {
      ...data,
      assignmentDetails,
    };
  };

  const getByIdForCopying = async (id: string) => {
    const { assignmentDetails, ...data } = await getById(id);

    if (assignmentDetails?.currentOwner) {
      const employeeName = assignmentDetails.currentOwnerName;
      assignmentDetails.currentOwnerFullName = `${assignmentDetails.currentOwner} ${
        employeeName?.firstName || ''
      } ${employeeName?.lastName || ''}`.trim();
    }

    return {
      category: data.category,
      type: data.type,
      office: data.office,
      name: data.name,
      condition: data.condition,
      serialNumber: data.serialNumber,
      characteristics: data.characteristics,
      attribution: data.attribution,
      cost: {
        ...data.costInPurchaseCurrency,
        currency: data.costInPurchaseCurrency.currency === 'BYR' ? 'BYN' : data.costInPurchaseCurrency.currency,
      },
      customUsefulLifePeriod: data.usefulLifePeriod,
      purchaseDate: data.purchaseDate,
      validityStartDate: data.validityStartDate,
      validityEndDate: data.validityEndDate,
      warrantyEndDate: data.warrantyEndDate,
      subscriptionDetails: data.subscriptionDetails,
      assignmentDetails,
    };
  };

  const performAssetCostProrating = (costProratingDate: string) =>
    sendPostRequest(`${RESOURCE_URL}/cost-prorating/perform`, { costProratingDate });

  const updateAssignmentDetails = async (
    inventoryNumberId: string,
    { assignmentDetails: initialAssignmentDetails }: any,
  ) => {
    let assignmentDetails = { ...(initialAssignmentDetails || {}) };

    if (assignmentDetails.currentOwner) {
      delete assignmentDetails.currentOwnerName;
      delete assignmentDetails.currentOwnerFullName;
    } else {
      assignmentDetails = null;
    }

    const response = await sendPutRequest(`${RESOURCE_URL}/${inventoryNumberId}/assignment-details`, {
      assignmentDetails,
    });

    return response.json();
  };

  const runMigration = async (data: Dictionary<string>) => {
    return sendPostRequest(`${RESOURCE_URL}/migration`, data);
  };

  const disposeAsset = async (inventoryNumber: string) => {
    const res = await sendPostRequest(`${RESOURCE_URL}/${inventoryNumber}/dispose`);
    return res.json();
  };

  const writeOffAsset = async ({
    inventoryNumber,
    expenseAccountId,
    budgetCategory,
  }: {
    inventoryNumber: string;
    expenseAccountId: string;
    budgetCategory: string;
  }) => {
    const res = await sendPostRequest(`${RESOURCE_URL}/${inventoryNumber}/write-off`, {
      expenseAccountId,
      budgetCategory,
    });
    return res.json();
  };

  const update = async (inventoryNumber: StringOrNumber, { cost, ...data }: any) => {
    return initialUpdate(inventoryNumber, {
      ...data,
      cost: {
        ...cost,
        currency: cost.currency === 'BYN' ? 'BYR' : cost.currency,
      },
    });
  };

  return {
    ...baseCrud,
    create,
    disposeAsset,
    writeOffAsset,
    getValidationSchema,
    getBalanceAccounts,
    getExpenseAccounts,
    getBusinessRules,
    getById,
    getByIdForCopying,
    performAssetCostProrating,
    search,
    updateAssignmentDetails,
    getUpdateValidationSchema,
    runMigration,
    searchTimelineItems,
    createTimelineItem,
    update,
    getPermissions,
    getBusinessRule,
  };
};

export default useAssetService;
